import { withErrorBoundary } from "@sentry/nextjs";
import { gql } from "graphql-request";
import { VFC } from "react";
import styled from "styled-components";
import { LegacyImage, Markdown } from "components";
import { PipelineRoutingLinkButton } from "components/Molecules/PipelineRoutingLinkButton";
import { useAnchor, useCTAButtonLink } from "hooks";
import { FullArrow } from "icons";
import { Mail } from "icons/Mail";
import { Phone } from "icons/Phone";
import { getImageFormat } from "lib/utils";
import { DataTestId } from "types/enums/testId";
import { Cta4Content } from "types/graphql/generated";

interface Props {
  content: Cta4Content;
}

const Cta4: VFC<Props> = withErrorBoundary(
  ({
    content: {
      cta4Title,
      cta4IconList,
      cta4ButtonLabel,
      cta4ButtonLink,
      cta4SubTitle,
      cta4MobileNumberList,
      cta4EmailAddressList,
    },
  }) => {
    useAnchor("cta4");

    return (
      <StyledCta4 className="cta4 Content">
        <div className="Cta4__content">
          <div className="Cta4__container">
            <ul className="Cta4__iconList">
              {cta4IconList.map(
                (
                  {
                    icon: {
                      data: {
                        icon: { ext, ...icon },
                      },
                    },
                  },
                  id
                ) => (
                  <li key={id} className="cta4__iconListTest">
                    <LegacyImage
                      {...icon}
                      layout="fill"
                      transform={{ format: getImageFormat(ext) }}
                      className="Cta4__headIcon"
                      style={{ zIndex: `${cta4IconList.length - Number(id)}` }}
                    />
                  </li>
                )
              )}
            </ul>
            <Markdown className="Cta4__title Font_48_54_600 lg:Font_40_48_600 md:Font_32_40_600">{cta4Title}</Markdown>

            <PipelineRoutingLinkButton
              href={useCTAButtonLink(cta4ButtonLink)}
              className="HeroSubsection4__button"
              dataTestId={DataTestId.Cta4Button}
              rightIcon={<FullArrow />}
            >
              {cta4ButtonLabel}
            </PipelineRoutingLinkButton>
          </div>
          <div className="Cta4__subSection">
            <h3 className="Cta4__subTitle Font_24_32_600 lg:Font_16_32_600">{cta4SubTitle}</h3>
            <ul className="Cta4__phoneList">
              {cta4MobileNumberList.map(({ timeSchedule, phone }) => (
                <li key={phone} className="cta4__phoneListItem">
                  <Phone className="Cta4__icon" />
                  <div className="Cta4__phoneNumber__container">
                    <Markdown className="Cta4__phoneNumber Font_24_32_600 lg:Font_16_32_600">{phone}</Markdown>
                    {!!timeSchedule && (
                      <p className="Cta4__timeSchedule Color_tertiary Font_14_20_450">{timeSchedule}</p>
                    )}
                  </div>
                </li>
              ))}
            </ul>
            <ul className="Cta4__mailList">
              {cta4EmailAddressList.map(({ email }) => (
                <li key={email} className="Cta4__mailListItem">
                  <Mail className="Cta4__icon" />
                  {!!email && <Markdown className=" Font_24_32_600 lg:Font_16_32_600">{email}</Markdown>}
                </li>
              ))}
            </ul>
          </div>
        </div>
      </StyledCta4>
    );
  },
  {}
);

const StyledCta4 = styled.section`
  position: relative;
  margin-bottom: -70px;
  padding-top: 64px;
  margin-top: -64px;

  .Cta4__content {
    background:
      url("/images/noise.png"),
      radial-gradient(
          59.96% 84.56% at 108.46% 114.67%,
          rgb(126 100 251 / 75%) 14.44%,
          rgb(255 205 133 / 75%) 59.75%,
          rgb(246 243 241 / 0%) 100%
        )
        /* warning: gradient uses a rotation that is not supported by CSS and may not behave as expected */,
      #faf7f4;
    background-blend-mode: overlay, normal, normal;
    background-repeat: no-repeat;
    padding: 80px 48px 48px;
  }

  .Cta4__container {
    padding-bottom: 48px;
    box-shadow: 0 1px 0 ${({ theme }) => theme.colors.fog["30"]};
  }

  .Cta4__iconList {
    display: flex;
    background-color: rgb(20 20 20 / 8%);
    border-radius: 100px;
    width: fit-content;
    padding: 10px;
  }

  .Cta4__iconList li:not(:first-child) {
    margin-left: -4px;
  }

  .Cta4__headIcon {
    position: relative;
    width: 64px;
    height: 64px;
  }

  .Cta4__icon {
    position: relative;
    min-width: 24px;
    height: 24px;
    margin-right: 16px;
    margin-top: 4px;
    border-radius: 50%;
    overflow: hidden;
  }

  .Cta4__title {
    margin-top: 24px;
    max-width: 928px;
  }

  .HeroSubsection4__button {
    width: fit-content;
    margin-top: 40px;
  }

  .Cta4__subSection {
    display: flex;
    margin-top: 48px;
    gap: 80px;
    align-items: flex-start;
  }

  .cta4__phoneListItem {
    display: flex;
  }

  .Cta4__mailListItem {
    display: flex;
  }

  .Cta4__phoneList {
    display: flex;
  }

  .Cta4__phoneNumber {
    display: flex;
    align-items: center;

    &__container {
      display: flex;
      flex-direction: column;
      gap: 4px;
    }

    a {
      text-decoration: none;
    }
  }

  .Cta4__timeSchedule {
    margin-top: 4px;
  }

  .cta4__phoneListItem:not(:first-child),
  .Cta4__mailListItem:not(:first-child) {
    margin-top: 16px;
  }

  @media (min-width: 1200px) {
    .Cta4__content {
      background-size: 100% 100%;
    }
  }

  @media (max-width: ${({ theme }) => `${theme.breakpoints.xl}px`}) {
    margin-bottom: -168px;
  }

  @media (max-width: ${({ theme }) => `${theme.breakpoints.lg}px`}) {
    margin-bottom: -156px;

    .Cta4__subSection {
      gap: 40px;
    }
  }

  @media (max-width: ${({ theme }) => `${theme.breakpoints.md}px`}) {
    margin-bottom: 0;

    &.Content {
      padding-left: 0;
      padding-right: 0;
    }

    .Cta4__content {
      padding: 80px 16px 64px;
    }

    .Cta4__subSection {
      flex-direction: column;
      gap: 24px;
    }
  }
`;

gql`
  fragment Cta4Content on ComponentCtaCta4 {
    cta4Title
    cta4IconList {
      id
      icon {
        data {
          icon: attributes {
            src: hash
            alt: alternativeText
            ext
          }
        }
      }
    }
    cta4ButtonLabel
    cta4ButtonLink
    cta4SubTitle
    cta4MobileNumberList {
      timeSchedule
      phone
    }
    cta4EmailAddressList {
      email
    }
  }
`;

export { Cta4 };
export type { Props as Cta4Props };
